import _ from 'lodash';
import { AfterViewInit, Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { PopoverService } from 'app/core/services/popover.service';
import { SharedService } from 'app/virtual-event/services/shared.service';
import { loadOrgDonations, loadOrgUser, resetDashboard } from '../../features/dashboard.actions';
import { selectOrgDonations, selectOrgUser } from '../../features/dashboard.selectors';
import { delay, filter, first, map, share, takeUntil } from 'rxjs/operators';
import { loadSurvey, resetSurvey } from '../../features/survey.actions';
import { selectSurveyShow } from '../../features/survey.selectors';
import { CookieService } from 'ngx-cookie-service';
import { SurveyService } from '../../services/survey.service';
import { Subject } from 'rxjs';
import { MdbModalRef } from 'mdb-angular-ui-kit/modal';
import { SurveyModalComponent } from '../modals/survey-modal/survey-modal.component';
import { Router } from '@angular/router';
import { RouteReuseStrategyService } from 'app/router-wrapper/services/route-reuse-strategy-service/route-reuse-strategy-service.service';
import { MdbModalWrapperService } from 'app/core/services/mdb-modal-wrapper.service';
import { AuthService } from 'app/router-wrapper/services/auth/auth.service';

@Component({
  selector: 'app-dashboard',
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.scss']
})
export class DashboardComponent implements OnInit, AfterViewInit, OnDestroy {

  @HostListener('document:click', ['$event'])
  onHidePopover(event: MouseEvent) {
    if (window && window.getSelection && window.getSelection().toString().length) {
      return;
    }
    const { list } = this.popoverService;
    for(let key in list) {
      const popover = list[key];
      popover.hide();
    }
  }

  public orgDnations; 
  public datasets;
  public labels;
  public activeBtn;
  public activeBtnName;
  public chartData;
  public user;
  public showChart;
  public initialized = false;
  public takeUntilSubject: Subject<any> = new Subject();
  public takeUntilRouteReuseStrategySubject: Subject<any> = new Subject();


  public options = {
    responsive: true,
    interaction: {
      mode: 'index',
      intersect: false,
    },
    plugins: {
      legend: {
          display: false,
      }
    },
    stacked: false,
    scales: {
      y: {
        type: 'linear',
        display: true,
        grid: {
          display: false,
          drawBorder: false,
          color: 'rgba(0, 0, 0, 0)',
        },
        ticks: {
          callback: function(value, index, values) {
              let newValue = Math.round(value)
              return window['utils'].getAmountDisplay(newValue)
          }
        },
      },
      x: {
        display: true,
        grid: {
          display: false,
          drawBorder: false,
          color: 'rgba(0, 0, 0, 0)',
        }
      }
    }
  };

  public orgUser = null;
  public orgUser$ = this.store.pipe(
    select(selectOrgUser),
    filter(user => !_.isNull(user)),
    filter((user: any) => {
      const { orgUser } = this;
      if(orgUser === null) {
        return true;
      }
      const { id: previousUserId, orgId: previousOrgId } = orgUser.loggedInUser || orgUser;
      const { id: currentUserId, orgId: currentOrgId } = user.loggedInUser || user;
      return previousUserId === currentUserId && previousOrgId === currentOrgId ? false: true;
    })
  );

  public showSeeAll = false;
  public showSeeAll$ = this.store.pipe(
    select(selectOrgUser),
    filter(user => !_.isNull(user)),
    map((user: any) => {
      const { access } = user.loggedInUser || {
        access: [1]
      };
      const { length } = _.intersection([1,2,4], access);
      return length > 0;
    })
  );

  public orgDonations$ = this.store.pipe(
    select(selectOrgDonations),
    filter(orgDnations => !_.isNull(orgDnations)),
    takeUntil(this.takeUntilSubject)
  );

  public surveyShow$ = this.store.pipe(
    select(selectSurveyShow),
    filter(show => !_.isNull(show)),
    takeUntil(this.takeUntilSubject)
  );

  constructor (
    private authService: AuthService,
    private modalService: MdbModalWrapperService,
    private surveyService: SurveyService,
    private popoverService: PopoverService,
    private cookieService: CookieService,
    private sharedService: SharedService,
    private store: Store,
    private router: Router,
    private routeReuseStrategyService: RouteReuseStrategyService,
  ) { }

  ngOnInit(): void {
    this.routeReuseStrategyService.status = true;
    this.routeReuseStrategyService.routeReuseStrategySubject.pipe(
      takeUntil(this.takeUntilRouteReuseStrategySubject)
    ).subscribe(() => {
      this.takeUntilSubject.next(null);
      this.takeUntilSubject.complete();
      this.takeUntilSubject = new Subject();

      this.ngAfterViewInit();
    });

    this.orgUser$.pipe(
      takeUntil(this.takeUntilSubject)
    ).subscribe(user => {
      this.orgUser = user;

      this.getAllDonationsMTD();
    });

    this.showSeeAll$.pipe(
      takeUntil(this.takeUntilSubject)
    ).subscribe(showSeeAll => {
      this.showSeeAll = showSeeAll;
    })
  }

  ngAfterViewInit(): void {
    this.surveyShow$.pipe(
      first(),
    ).subscribe(() => {
      this.initialized = true;
    })

    this.orgDonations$.pipe(
      takeUntil(this.takeUntilSubject)
    ).subscribe(orgDnations=>{  
      this.orgDnations = orgDnations
      if(this.orgDnations){
        if(this.orgDnations.chartData == null){
          this.chartData = {
            bankAccountDonations:[],
            cardDonations:[],
            cashDonations:[],
            checkDonations:[],
            labels:[],
            totalDonations:[],
            totalPledges:[]
          }
        }else{
          this.chartData = this.orgDnations.chartData
        }
  
        if(this.chartData.totalDonations.length == 1 ){
          this.chartData.totalDonations.unshift(0)
          this.chartData.labels.unshift(' ')
        }
  
        if(this.chartData.totalDonations.length != 0 ){
          this.showChart = true
          this.datasets = [
            {
              data: [...this.chartData.totalDonations],
              type: 'line',
              backgroundColor: 'RGBA(195, 184, 250, .7)',
              borderColor: '#542BE7',
              borderWidth: 2,
              pointBorderColor: '#542BE7',
              pointBackgroundColor: 'RGB(195, 184, 250)',
              fill: true,
            },
          ];
        
          this.labels = [...this.chartData.labels];
        } else{
          this.showChart = false
        }
      }

      this.getSurveyStatus();
    });

    this.surveyShow$.pipe(
      filter(() => {
        return this.cookieService.get('adbAccessToken') === "";
      }),
      filter(show => show),
      delay(1000),
      takeUntil(this.takeUntilSubject)
    ).subscribe(() => {
      this.surveyService.modalRef = this.modalService.open(SurveyModalComponent, {
        ignoreBackdropClick: true,
        modalClass:'modal--paybee modal-lg modal--survey',
        containerClass:'mdb-modal-backdrop-paybee mdb-modal-backdrop-paybee--survey',
      });
    })

    if(this.sharedService.token) {
      this.getCurrentUser()
    }
  }

  ngOnDestroy(): void {
    this.takeUntilSubject.next(null);
    this.takeUntilSubject.complete();

    this.takeUntilRouteReuseStrategySubject.next(null);
    this.takeUntilRouteReuseStrategySubject.complete();

    this.routeReuseStrategyService.status = false;

    if(this.surveyService.modalRef instanceof MdbModalRef) {
      try {
        this.surveyService.modalRef.close();
      } catch(e) {}
      this.surveyService.modalRef = null;
    }

    this.store.dispatch({
      type: resetDashboard.type
    });

    this.store.dispatch({
      type: resetSurvey.type
    });
  }

  getCurrentUser(){
    this.store.dispatch({
      type:loadOrgUser.type,
      payload: {
        reload: this.authService.isFirstRun ? false: true
      }
    });
  }

  getSurveyStatus(){
    this.store.dispatch({
      type:loadSurvey.type,
    });
  }

  getAllDonationsMTD(){
    this.activeBtn = 'mtd'
    this.activeBtnName = 'Month To Date'
    var startDate = Math.floor(Math.floor((new Date(new Date().setDate(1)).setHours(0,0,0)/1000)))
    var endDate = Math.floor(new Date(new Date().setHours(0,0,0)).getTime()/1000) + 24*60*60 - 1

    this.store.dispatch({
      type:loadOrgDonations.type,
      payload:{
        token:this.sharedService.token,
        startDate,
        endDate
      }
    })
  }

  getAllDonationsYTD(){
    this.activeBtn = 'ytd'
    this.activeBtnName = 'Year To Date'

    const now = new Date();
    const yearStart = new Date(now.getFullYear(), 0, 1);
    const yearStartTimestamp = Math.floor(yearStart.getTime() / 1000);

    var startDate = yearStartTimestamp
    var endDate = Math.floor(new Date(new Date().setHours(0,0,0)).getTime()/1000) + 24*60*60 - 1

    this.store.dispatch({
      type:loadOrgDonations.type,
      payload:{
        token:this.sharedService.token,
        startDate,
        endDate
      }
    })
  }
  
  getAllDonationsLastMonth(){
    this.activeBtn = 'month'
    this.activeBtnName = 'Last Month'


    const lastMonth = new Date(new Date().getFullYear(), new Date().getMonth() - 1, 1);
    const startUnixTimestamp = Math.floor(lastMonth.getTime() / 1000);

    const currentDate = new Date();
    const lastDayOfPreviousMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 0);
    const endUnixTimestamp = Math.floor(lastDayOfPreviousMonth.getTime() / 1000) + 86400 - 1;

    var startDate = startUnixTimestamp
    var endDate = endUnixTimestamp

    this.store.dispatch({
      type:loadOrgDonations.type,
      payload:{
        token:this.sharedService.token,
        startDate,
        endDate
      }
    })
  }

  getAllDonationsLastYear(){
    this.activeBtn = 'year'
    this.activeBtnName = 'Last Year'
    var currentDate = new Date()
    var startDate = Math.floor(new Date(currentDate.getFullYear() - 1, 0, 1).getTime()/1000)
    var endDate = Math.floor(new Date(new Date(currentDate.getFullYear() - 1, 11, 31)).setHours(0,0,0)/1000 + 24*60*60 - 1)

    this.store.dispatch({
      type:loadOrgDonations.type,
      payload:{
        token:this.sharedService.token,
        startDate,
        endDate
      }
    })
  }

  onDonationClick(){
    this.router.navigateByUrl('/reportAll')
  }

  onPendingClick(){
    this.router.navigateByUrl('/reportPendingCarts')
  }
  
  getAmount(anount){
    let newAmount = Math.round(anount)
    return window['utils'].getAmountDisplay(newAmount);
  }

}
